import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
    Popover,
    TextField,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Divider,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import UserRole from '../../../shared/AuthRoles';
import {
    selectCalendarViewReservationData,
    openCalendarViewReservationAction,
} from '../../../app/Slices/CalendarViewSlice';
import {
    selectReservationFilters,
    setReservationFilters,
} from '../../../app/Slices/ReservationFiltersSlice';
import {
    useGetAllReservationsQuery,
    useGetAllWaitlistRequestsQuery,
    useGetProfileDetailsQuery,
    useGetReportQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import ViewReservationListComponent from '../../components/Reservations/ViewReservations/ViewReservationListComponent';
import ViewReservationsCalendarComponent from '../../components/Reservations/ViewReservations/ViewReservationsCalendarComponent';
import ReportLoadingComponent from '../../../layout/styledcomponents/LoadingAnimations/ReportLoadingComponent';
import ReservationCalendarModal from '../../components/Reservations/ViewReservations/ReservationCalendarModal';
import { Status } from '../../enums/booking';
import { alpha } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { capitalizeName } from '../../../shared/utils';
import FormAutocomplete from '../../shared/FormAutocomplete';
import FormMultiAutocomplete from '../../shared/FormMultiAutocomplete';
import type { Theme } from '@mui/material/styles';
import jsPDF from 'jspdf';
import moment from 'moment';

const getCss = (theme: Theme) => `
  .rdp-root {
    --rdp-accent-color: ${theme.palette.primary.main};
    --rdp-accent-background-color: ${alpha(theme.palette.primary.main, 0.08)};
    --rdp-day_button-width: 40px;
    --rdp-day_button-height: 40px;
    --rdp-day-width: 40px;
    --rdp-day-height: 40px;
    font-size: 14px;
    color: ${theme.palette.text.primary};
  }

  .rdp-range_middle {
    color: ${theme.palette.primary.main};
  }

  .rdp-selected {
    font-size: 14px;
  }

  .rdp-footer {
    font-size: 14px;
  }

  .rdp-months {
    flex-direction: row;
    gap: 24px;
  }

  @media (max-width: 770px) {
    .rdp-months {
      flex-direction: column;
      gap: 0px;
    }
  }
`;

const formatDmy = (ymd?: string) => {
    if (!ymd) return 'Any';
    return format(parseISO(ymd), 'dd/MM/yyyy');
};

const formatPretty = (ymd?: string) => {
    if (!ymd) return 'Any';
    return format(parseISO(ymd), 'd MMM yyyy');
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box pt={0}>{children}</Box>}
        </div>
    );
};

type AnyReservation = {
    status?: string;
    startDate?: string;
    endDate?: string;
};

const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = (day + 6) % 7;

    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - diffToMonday);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return { weekStart, weekEnd };
};

const overlapsWeek = (
    reservation: AnyReservation,
    weekStart: Date,
    weekEnd: Date,
) => {
    const start = reservation?.startDate
        ? new Date(reservation.startDate)
        : undefined;
    const end = reservation?.endDate ? new Date(reservation.endDate) : start;

    if (!start) return false;

    const effectiveEnd = end ?? start;
    return start <= weekEnd && effectiveEnd >= weekStart;
};

const makeLabel = (key: string) => {
    if (key === 'moduleType') return 'Module';
    if (key === 'visitType') return 'Visit';
    if (key === 'bookerType') return 'Booker';
    if (key === 'guest') return 'Guest';
    return key;
};

const parseYmd = (value?: string) => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const inDateRange = (r: any, from?: string, to?: string) => {
    const fromDate = parseYmd(from);
    const toDate = parseYmd(to);

    if (!fromDate && !toDate) return true;

    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    if (toDate) toDate.setHours(23, 59, 59, 999);

    const start = r?.startDate ? new Date(r.startDate) : null;
    if (!start || Number.isNaN(start.getTime())) return false;

    const end = r?.endDate ? new Date(r.endDate) : start;
    if (end && Number.isNaN(end.getTime())) return false;

    const rangeStart = fromDate ?? new Date(-8640000000000000);
    const rangeEnd = toDate ?? new Date(8640000000000000);

    return start <= rangeEnd && end >= rangeStart;
};

export default function ReservationPage() {
    const loggedInUser = UserRole();
    const isGuest = Boolean(loggedInUser?.role?.includes('guest'));
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isLaptop = useMediaQuery('(max-width: 1550px)');
    const isSmallTabs = isMobile || isIpad;

    const dispatch = useDispatch();
    const filters = useSelector(selectReservationFilters);

    type InlineFilters = {
        moduleType?: any[];
        bookerType?: 'owner' | 'guest';
        guest?: number;
        visitType?: 'business' | 'personal';
    };

    const methods = useForm<InlineFilters>({
        defaultValues: {
            moduleType: filters?.moduleType ?? [],
            bookerType: filters?.bookerType,
            guest: filters?.guest,
            visitType: filters?.visitType,
        },
    });

    const { control, reset, setValue } = methods;

    useEffect(() => {
        reset({
            moduleType: filters?.moduleType ?? [],
            bookerType: filters?.bookerType,
            guest: filters?.guest,
            visitType: filters?.visitType,
        });
    }, [
        filters?.moduleType,
        filters?.bookerType,
        filters?.guest,
        filters?.visitType,
        reset,
    ]);

    const watched = useWatch({ control });

    const sameArray = (a: any[] = [], b: any[] = []) =>
        a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

    useEffect(() => {
        const nextModule = watched?.moduleType ?? [];
        const nextBooker = watched?.bookerType;
        const nextGuest = watched?.guest;
        const nextVisit = watched?.visitType;

        const changed =
            !sameArray(filters?.moduleType ?? [], nextModule) ||
            (filters?.bookerType ?? undefined) !== (nextBooker ?? undefined) ||
            (filters?.guest ?? undefined) !== (nextGuest ?? undefined) ||
            (filters?.visitType ?? undefined) !== (nextVisit ?? undefined);

        if (!changed) return;

        dispatch(
            setReservationFilters({
                ...filters,
                moduleType: nextModule,
                bookerType: nextBooker,
                guest: nextGuest,
                visitType: nextVisit,
            }),
        );
    }, [
        watched?.moduleType,
        watched?.bookerType,
        watched?.guest,
        watched?.visitType,
        filters?.moduleType,
        filters?.bookerType,
        filters?.guest,
        filters?.visitType,
        dispatch,
        filters,
    ]);

    const { data: guestCompanies } = useGetReportQuery(
        `/reports/guest-details`,
        {
            skip: loggedInUser?.role.includes('guest'),
        },
    );

    const calendarView = useSelector(selectCalendarViewReservationData);
    const view = calendarView.view;
    const location = useLocation();
    const navigate = useNavigate();
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');

    const [filtersReady, setFiltersReady] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const isCalendar = params.get('view') === 'calendar';
        if (calendarView.view !== isCalendar) {
            dispatch(openCalendarViewReservationAction({ view: isCalendar }));
        }
    }, [location.search, calendarView.view, dispatch]);

    const [searchValue, setSearchValue] = useState(
        sessionStorage.getItem('searchValue') || '',
    );

    const onSearchChange = (v: string) => {
        setSearchValue(v);
        sessionStorage.setItem('searchValue', v);
    };

    const clearSearch = () => {
        setSearchValue('');
        sessionStorage.removeItem('searchValue');
    };

    const [datePopoverAnchor, setDatePopoverAnchor] =
        useState<HTMLElement | null>(null);

    const selectedRange = useMemo<DateRange | undefined>(() => {
        const from = filters?.dateFrom ? new Date(filters.dateFrom) : undefined;
        const to = filters?.dateTo ? new Date(filters.dateTo) : undefined;
        if (!from && !to) return undefined;
        return { from, to };
    }, [filters?.dateFrom, filters?.dateTo]);

    const openDatePopover = (e: React.MouseEvent<HTMLElement>) => {
        setDatePopoverAnchor(e.currentTarget);
    };

    const closeDatePopover = () => setDatePopoverAnchor(null);
    const isDatePopoverOpen = Boolean(datePopoverAnchor);

    const [downloadAnchorEl, setDownloadAnchorEl] =
        useState<HTMLElement | null>(null);

    const openDownload = (e: React.MouseEvent<HTMLElement>) =>
        setDownloadAnchorEl(e.currentTarget);

    const closeDownload = () => setDownloadAnchorEl(null);
    const isDownloadOpen = Boolean(downloadAnchorEl);

    const handleSelectRange = (range: DateRange | undefined) => {
        const from = range?.from ? format(range.from, 'yyyy-MM-dd') : undefined;
        const to = range?.to ? format(range.to, 'yyyy-MM-dd') : from;

        dispatch(
            setReservationFilters({
                ...filters,
                dateFrom: from,
                dateTo: to,
            }),
        );
    };

    const OWNER_TAB_ALL = 0;
    const OWNER_TAB_PENDING = 1;
    const OWNER_TAB_ARRIVING = 2;
    const OWNER_TAB_CHECKOUT_THIS_WEEK = 3;
    const OWNER_TAB_CONFIRMED = 4;
    const OWNER_TAB_WAITLIST = 5;
    const OWNER_TAB_W_LIST_ARCHIVE = 6;

    const GUEST_TAB_ALL = 0;
    const GUEST_TAB_PENDING = 1;
    const GUEST_TAB_CHECKOUT_THIS_WEEK = 2;
    const GUEST_TAB_CONFIRMED = 3;
    const GUEST_TAB_WAITLIST = 4;
    const GUEST_TAB_W_LIST_ARCHIVE = 5;
    const tabs = useMemo(
        () => [
            {
                key: 'all',
                label: 'All',
                guestIndex: GUEST_TAB_ALL,
                ownerIndex: OWNER_TAB_ALL,
            },
            {
                key: 'pending',
                label: 'Pending',
                guestIndex: GUEST_TAB_PENDING,
                ownerIndex: OWNER_TAB_PENDING,
            },
            ...(!isGuest
                ? [
                      {
                          key: 'arriving',
                          label: 'Arriving',
                          ownerIndex: OWNER_TAB_ARRIVING,
                      },
                  ]
                : []),
            ...(!isGuest
                ? [
                      {
                          key: 'checkingOut',
                          label: 'Checking Out',
                          ownerIndex: OWNER_TAB_CHECKOUT_THIS_WEEK,
                      },
                  ]
                : []),
            {
                key: 'confirmed',
                label: 'Confirmed',
                guestIndex: GUEST_TAB_CONFIRMED,
                ownerIndex: OWNER_TAB_CONFIRMED,
            },
            {
                key: 'waitlist',
                label: 'Waitlist',
                guestIndex: GUEST_TAB_WAITLIST,
                ownerIndex: OWNER_TAB_WAITLIST,
            },
        ],
        [isGuest],
    );

    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        const fallback = tabs[0]?.ownerIndex ?? OWNER_TAB_ALL;
        const tab = parseInt(params.get('tab') || String(fallback));
        if (isNaN(tab)) return fallback;

        if (isGuest && tab === GUEST_TAB_CHECKOUT_THIS_WEEK)
            return GUEST_TAB_ALL;

        return tab;
    };

    const initialTab = getInitialTab();

    const ownerStatusMap = useMemo<Record<number, Status[] | undefined>>(
        () => ({
            [OWNER_TAB_ALL]: undefined,
            [OWNER_TAB_PENDING]: [Status.Pending],
            [OWNER_TAB_ARRIVING]: [Status.Approved],
            [OWNER_TAB_CONFIRMED]: [Status.Approved],
            [OWNER_TAB_CHECKOUT_THIS_WEEK]: [Status.Approved],
            [OWNER_TAB_WAITLIST]: undefined,
        }),
        [],
    );

    const guestStatusMap = useMemo<Record<number, Status[] | undefined>>(
        () => ({
            [GUEST_TAB_ALL]: undefined,
            [GUEST_TAB_PENDING]: [Status.Pending],
            [GUEST_TAB_CONFIRMED]: [Status.Approved],
            [GUEST_TAB_CHECKOUT_THIS_WEEK]: [Status.Approved],
            [GUEST_TAB_WAITLIST]: undefined,
        }),
        [],
    );

    const statusMap = useMemo(
        () => (isGuest ? guestStatusMap : ownerStatusMap),
        [isGuest, guestStatusMap, ownerStatusMap],
    );

    const waitlistTab = isGuest ? GUEST_TAB_WAITLIST : OWNER_TAB_WAITLIST;

    const sameStatus = (a: Status[] | undefined, b: Status[] | undefined) => {
        if (a === b) return true;
        if (!a && !b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
        return true;
    };

    const [tabValue, setTabValue] = useState<number>(initialTab);

    useEffect(() => {
        if (isGuest && tabValue === GUEST_TAB_CHECKOUT_THIS_WEEK) {
            setTabValue(GUEST_TAB_ALL);
            const params = new URLSearchParams(location.search);
            params.set('tab', String(GUEST_TAB_ALL));
            navigate({ search: params.toString() }, { replace: false });
        }
    }, [isGuest, tabValue, location.search, navigate]);

    useEffect(() => {
        if (view) {
            if (!filtersReady) setFiltersReady(true);
            return;
        }

        const nextStatus =
            tabValue === waitlistTab ? filters.status : statusMap[tabValue];

        if (!sameStatus(filters.status, nextStatus)) {
            dispatch(setReservationFilters({ ...filters, status: nextStatus }));
        }

        if (!filtersReady) setFiltersReady(true);
    }, [
        view,
        tabValue,
        waitlistTab,
        statusMap,
        filters.status,
        filtersReady,
        dispatch,
        filters,
    ]);

    const initialFilters = {
        ...filters,
        status:
            view ||
            initialTab === (isGuest ? GUEST_TAB_WAITLIST : OWNER_TAB_WAITLIST)
                ? filters.status
                : statusMap[initialTab],
    };

    const { data: reservations, isFetching } = useGetAllReservationsQuery(
        {
            ...initialFilters,
            sort: 'startDate',
        },
        { skip: !filtersReady },
    );

    const waitlistArchiveTabIndex = isGuest
        ? GUEST_TAB_W_LIST_ARCHIVE
        : OWNER_TAB_W_LIST_ARCHIVE;

    const waitlistStatusParam =
        tabValue === waitlistArchiveTabIndex
            ? ['expired', 'cancelled']
            : ['active'];

    const { data: waitListRequests, isFetching: isFetchingWaitlistRequests } =
        useGetAllWaitlistRequestsQuery(
            {
                ...initialFilters,
                status: waitlistStatusParam,
                sort: 'startDate',
            },
            { skip: !filtersReady },
        );

    const { data: activeWaitlistTotals } = useGetAllWaitlistRequestsQuery(
        { status: ['active'], sort: 'startDate' },
        { skip: !filtersReady },
    );

    const { data: archiveWaitlistTotals } = useGetAllWaitlistRequestsQuery(
        { status: ['expired', 'cancelled'], sort: 'startDate' },
        { skip: !filtersReady },
    );

    const { data: getReservationTotals } = useGetAllReservationsQuery({
        ...filters,
        status: undefined,
        sort: 'startDate',
    });

    const getTotalByStatus = (status: string): number => {
        return (
            getReservationTotals?.filter(
                (reservation: { status: string }) =>
                    reservation.status === status,
            )?.length || 0
        );
    };

    const { weekStart, weekEnd } = useMemo(() => getCurrentWeekRange(), []);
    const endsThisWeek = (
        reservation: AnyReservation,
        weekStart: Date,
        weekEnd: Date,
    ) => {
        const end = reservation?.endDate
            ? new Date(reservation.endDate)
            : reservation?.startDate
              ? new Date(reservation.startDate)
              : null;

        if (!end || Number.isNaN(end.getTime())) return false;

        return end >= weekStart && end <= weekEnd;
    };

    const arrivingCount = useMemo(() => {
        if (isGuest) return 0;
        const list = (getReservationTotals || []) as AnyReservation[];
        return list.filter(
            (r) =>
                String(r?.status ?? '').toLowerCase() === 'approved' &&
                overlapsWeek(r, weekStart, weekEnd),
        ).length;
    }, [getReservationTotals, weekStart, weekEnd, isGuest]);

    const arrivingReservations = useMemo(() => {
        const list = (reservations || []) as AnyReservation[];
        return list.filter(
            (r) =>
                String(r?.status ?? '').toLowerCase() === 'approved' &&
                overlapsWeek(r, weekStart, weekEnd),
        );
    }, [reservations, weekStart, weekEnd]);

    const checkoutThisWeekCount = useMemo(() => {
        const list = (getReservationTotals || []) as AnyReservation[];
        return list.filter(
            (r) =>
                String(r?.status ?? '').toLowerCase() === 'approved' &&
                endsThisWeek(r, weekStart, weekEnd),
        ).length;
    }, [getReservationTotals, weekStart, weekEnd]);

    const checkoutThisWeekReservations = useMemo(() => {
        const list = (reservations || []) as AnyReservation[];
        return list.filter(
            (r) =>
                String(r?.status ?? '').toLowerCase() === 'approved' &&
                endsThisWeek(r, weekStart, weekEnd),
        );
    }, [reservations, weekStart, weekEnd]);

    const [totalPendingReservations, setTotalPendingReservations] = useState(0);
    const [totalConfirmedReservations, setTotalConfirmedReservations] =
        useState(0);
    const [allReservationTotal, setAllReservationTotal] = useState(0);
    const [totalWaitlistRequests, setTotalWaitlistRequests] = useState(0);
    const [totalWaitlistArchiveRequests, setTotalWaitlistArchiveRequests] =
        useState(0);

    useEffect(() => {
        if (location?.state?.page === 'waitlist') {
            setTabValue(isGuest ? GUEST_TAB_WAITLIST : OWNER_TAB_WAITLIST);
        }
    }, []);

    useEffect(() => {
        setTotalPendingReservations(getTotalByStatus('pending'));
        setTotalConfirmedReservations(getTotalByStatus('approved'));
        setTotalWaitlistRequests(activeWaitlistTotals?.length || 0);
        setTotalWaitlistArchiveRequests(archiveWaitlistTotals?.length || 0);
        setAllReservationTotal(getReservationTotals?.length || 0);
    }, [getReservationTotals, activeWaitlistTotals, archiveWaitlistTotals]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fallback = isGuest ? GUEST_TAB_ALL : OWNER_TAB_ALL;
        const tab = parseInt(params.get('tab') || String(fallback));
        if (!isNaN(tab) && tab !== tabValue) {
            if (isGuest && tab === GUEST_TAB_CHECKOUT_THIS_WEEK) return;
            setTabValue(tab);
        }
    }, [location.search, isGuest]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [tabValue]);

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        if (isGuest && newValue === GUEST_TAB_CHECKOUT_THIS_WEEK) return;

        setTabValue(newValue);
        const params = new URLSearchParams(location.search);
        params.set('tab', String(newValue));
        navigate({ search: params.toString() }, { replace: false });
    };

    const tabData = useMemo(() => {
        const items = isGuest
            ? [
                  {
                      label: 'All',
                      count: allReservationTotal,
                      index: GUEST_TAB_ALL,
                  },
                  {
                      label: 'Pending',
                      count: totalPendingReservations,
                      index: GUEST_TAB_PENDING,
                  },
                  {
                      label: 'Confirmed',
                      count: totalConfirmedReservations,
                      index: GUEST_TAB_CONFIRMED,
                  },
                  {
                      label: 'Waitlist',
                      count: totalWaitlistRequests,
                      index: GUEST_TAB_WAITLIST,
                  },
              ]
            : [
                  {
                      label: 'All',
                      count: allReservationTotal,
                      index: OWNER_TAB_ALL,
                  },
                  {
                      label: 'Pending',
                      count: totalPendingReservations,
                      index: OWNER_TAB_PENDING,
                  },
                  {
                      label: 'Arriving',
                      count: arrivingCount,
                      index: OWNER_TAB_ARRIVING,
                  },
                  {
                      label: 'Checking Out',
                      count: checkoutThisWeekCount,
                      index: OWNER_TAB_CHECKOUT_THIS_WEEK,
                  },
                  {
                      label: 'Confirmed',
                      count: totalConfirmedReservations,
                      index: OWNER_TAB_CONFIRMED,
                  },
                  {
                      label: 'Waitlist',
                      count: totalWaitlistRequests,
                      index: OWNER_TAB_WAITLIST,
                  },
              ];

        return items;
    }, [
        isGuest,
        allReservationTotal,
        totalPendingReservations,
        totalConfirmedReservations,
        totalWaitlistRequests,
        totalWaitlistArchiveRequests,
        arrivingCount,
        checkoutThisWeekCount,
    ]);

    const waitlistTabIndex = isGuest ? GUEST_TAB_WAITLIST : OWNER_TAB_WAITLIST;
    const waitlistSubTabData = useMemo(
        () => [
            {
                label: 'Active',
                count: totalWaitlistRequests,
                index: waitlistTabIndex,
            },
            {
                label: 'Archive',
                count: totalWaitlistArchiveRequests,
                index: waitlistArchiveTabIndex,
            },
        ],
        [
            totalWaitlistRequests,
            totalWaitlistArchiveRequests,
            waitlistTabIndex,
            waitlistArchiveTabIndex,
        ],
    );
    const checkoutThisWeekTabIndex = isGuest
        ? GUEST_TAB_CHECKOUT_THIS_WEEK
        : OWNER_TAB_CHECKOUT_THIS_WEEK;

    const tabsForUi = useMemo(() => {
        return tabData.filter(
            (t) =>
                !(isGuest && String(t.label).toLowerCase() === 'checking out'),
        );
    }, [tabData, isGuest]);

    const safeTabValue = useMemo(() => {
        if (tabValue === waitlistArchiveTabIndex)
            return waitlistArchiveTabIndex;
        const exists = tabsForUi.some((t) => t.index === tabValue);
        return exists
            ? tabValue
            : (tabsForUi[0]?.index ??
                  (isGuest ? GUEST_TAB_ALL : OWNER_TAB_ALL));
    }, [tabsForUi, tabValue, isGuest, waitlistArchiveTabIndex]);

    useEffect(() => {
        if (safeTabValue !== tabValue) {
            setTabValue(safeTabValue);
            const params = new URLSearchParams(location.search);
            params.set('tab', String(safeTabValue));
            navigate({ search: params.toString() }, { replace: false });
        }
    }, [safeTabValue]);

    const isWaitlistTabActive =
        safeTabValue === waitlistTabIndex ||
        safeTabValue === waitlistArchiveTabIndex;
    const safeMainTabValue =
        safeTabValue === waitlistArchiveTabIndex
            ? waitlistTabIndex
            : safeTabValue;
    const activeTabLabel = useMemo(() => {
        if (safeTabValue === waitlistArchiveTabIndex) return 'W/List Archive';
        return tabData.find((t) => t.index === safeTabValue)?.label || 'All';
    }, [safeTabValue, waitlistArchiveTabIndex, tabData]);

    const rawListReservations =
        tabValue === waitlistTabIndex || tabValue === waitlistArchiveTabIndex
            ? waitListRequests
            : tabValue === checkoutThisWeekTabIndex
              ? checkoutThisWeekReservations
              : !isGuest && tabValue === OWNER_TAB_ARRIVING
                ? arrivingReservations
                : reservations;

    const listReservations = useMemo(() => {
        const list = Array.isArray(rawListReservations)
            ? rawListReservations
            : [];

        return list.filter((r: any) => {
            if (!inDateRange(r, filters?.dateFrom, filters?.dateTo))
                return false;

            if (
                Array.isArray(filters?.moduleType) &&
                filters.moduleType.length > 0
            ) {
                const rowModuleType = String(
                    r?.module?.type ?? r?.moduleType ?? r?.listing?.type ?? '',
                ).toLowerCase();

                const matchesModuleType = filters.moduleType.some(
                    (value: any) =>
                        String(value).toLowerCase() === rowModuleType,
                );

                if (!matchesModuleType) return false;
            }

            if (filters?.guest) {
                const rowGuestId =
                    r?.guest?.id ??
                    r?.guestId ??
                    r?.company?.id ??
                    r?.bookingInfo?.guestId;

                if (String(rowGuestId) !== String(filters.guest)) return false;
            }

            if (!isWaitlistTabActive && filters?.visitType) {
                const rowVisitType = String(
                    r?.bookingInfo?.visitType ?? '',
                ).toLowerCase();

                if (rowVisitType !== String(filters.visitType).toLowerCase()) {
                    return false;
                }
            }

            return true;
        });
    }, [
        rawListReservations,
        filters?.dateFrom,
        filters?.dateTo,
        filters?.moduleType,
        filters?.guest,
        filters?.visitType,
        isWaitlistTabActive,
    ]);

    const safe = (v: any) =>
        String(v ?? '')
            .replace(/\s+/g, ' ')
            .trim();

    const getPrimaryUser = (row: any) =>
        row?.booker ?? row?.guest ?? row?.createdBy;

    const getPrimaryName = (row: any) => {
        const p = getPrimaryUser(row);
        const first = p?.firstName ?? '';
        const last = p?.lastName ?? '';
        const full = `${first} ${last}`.trim();
        return full || row?.guest?.name || row?.owner?.name || '-';
    };

    const getPrimaryEmail = (row: any) => {
        const p = getPrimaryUser(row);
        return p?.email ?? row?.bookingInfo?.email ?? '-';
    };

    const getCompanyName = (row: any) =>
        row?.guest?.name ??
        row?.owner?.name ??
        row?.company?.name ??
        row?.bookingInfo?.companyName ??
        row?.bookingInfo?.company ??
        '';

    const getGuestsCount = (row: any) =>
        row?.bookingInfo?.guestCount ??
        row?.bookingInfo?.guests ??
        row?.guestCount ??
        row?.guests ??
        row?.numberOfGuests ??
        row?.pax ??
        0;

    const getModuleName = (row: any) =>
        row?.module?.name ?? row?.listing?.name ?? '-';

    const getModuleType = (row: any) => {
        const t = row?.module?.type ?? row?.moduleType ?? row?.listing?.type;
        return t ? capitalizeName(t) : '-';
    };

    const fmtDate = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return '';
        return format(date, 'dd/MM/yyyy');
    };

    const escapeCsv = (value: any) => {
        const s = safe(value);
        const needsQuotes = /[",\n]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
    };

    const buildExportRows = () =>
        (listReservations || []).map((r: any) => ({
            Ref: r?.id ? `#${r.id}` : '',
            Guest: getPrimaryName(r),
            Email: getPrimaryEmail(r),
            Company: getCompanyName(r),
            Experience: getModuleName(r),
            Type: getModuleType(r),
            Guests: getGuestsCount(r),
            Visit: r?.bookingInfo?.visitType
                ? capitalizeName(r.bookingInfo.visitType)
                : 'Blocked',
            'Check-in': fmtDate(r?.startDate),
            Checkout: fmtDate(r?.endDate),
            Status: capitalizeName(String(r?.status ?? '')),
        }));

    const downloadCsv = () => {
        const rows = buildExportRows();
        if (!rows.length) return;

        const headers = Object.keys(rows[0] || {});
        const csv = [
            headers.join(','),
            ...rows.map((row) =>
                headers.map((h) => escapeCsv((row as any)[h])).join(','),
            ),
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `Reservations-${activeTabLabel}-${format(
            new Date(),
            'yyyy-MM-dd',
        )}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(url);
    };

    const downloadPdf = () => {
        const rows = buildExportRows();
        if (!rows.length) return;

        const doc: any = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const title = `Reservations (${safe(activeTabLabel)})`;
        const exportedAt = `Exported: ${moment().format('DD/MM/YYYY h:mm A')}`;

        const headers = Object.keys(rows[0] || {});
        const visibleHeaders = headers;
        const colWidths = [16, 26, 34, 28, 34, 18, 12, 18, 18, 18, 18];

        const left = 10;
        const right = 10;
        const top = 12;

        const usableWidth = pageWidth - left - right;

        const normWidths = (() => {
            const base = colWidths.slice(0, visibleHeaders.length);
            const sum = base.reduce((a, b) => a + b, 0) || 1;
            return base.map((w) => (w / sum) * usableWidth);
        })();

        const wrap = (text: string, maxWidth: number) => {
            const s = safe(text);
            if (!s) return [''];
            return doc.splitTextToSize(s, maxWidth) as string[];
        };

        const drawRow = (
            cells: string[],
            startY: number,
            isHeader: boolean,
        ) => {
            const cellPaddingY = 1.5;
            const cellPaddingX = 1.5;

            const wrapped = cells.map((c, i) =>
                wrap(c, normWidths[i] - cellPaddingX * 2),
            );
            const lineHeights = 4.2;
            const rowHeight =
                Math.max(...wrapped.map((w) => w.length)) * lineHeights +
                cellPaddingY * 2;

            if (startY + rowHeight > pageHeight - 10) {
                doc.addPage();
                return { y: top + 14, rowHeight, didPageBreak: true };
            }

            let x = left;
            if (isHeader) doc.setFont('helvetica', 'bold');
            else doc.setFont('helvetica', 'normal');

            doc.setFontSize(isHeader ? 9 : 8);

            wrapped.forEach((lines, i) => {
                const w = normWidths[i];
                doc.rect(x, startY, w, rowHeight);
                const textY = startY + cellPaddingY + 3;
                lines.forEach((line, idx) => {
                    doc.text(line, x + cellPaddingX, textY + idx * lineHeights);
                });
                x += w;
            });

            return { y: startY + rowHeight, rowHeight, didPageBreak: false };
        };

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(title, left, top);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(exportedAt, left, top + 6);

        let y = top + 12;

        const headerDraw = drawRow(visibleHeaders, y, true);
        y = headerDraw.y;

        rows.forEach((r: any) => {
            const cells = visibleHeaders.map((h) => safe(r[h]));
            const res = drawRow(cells, y, false);
            y = res.y;

            if (res.didPageBreak) {
                const headerAgain = drawRow(visibleHeaders, y, true);
                y = headerAgain.y;
            }
        });

        doc.save(
            `Reservations-${safe(activeTabLabel)}-${moment().format(
                'YYYY-MM-DD',
            )}.pdf`,
        );
    };

    const downloadDisabled = view || !listReservations?.length;
    const theme = useTheme();

    const appliedFilterChips = useMemo(() => {
        const chips: { key: string; label: string; value: string }[] = [];
        const anyFilters: any = filters || {};

        if (searchValue?.trim()) {
            chips.push({
                key: 'search',
                label: 'Search',
                value: searchValue.trim(),
            });
        }

        if (anyFilters?.dateFrom || anyFilters?.dateTo) {
            chips.push({
                key: 'dateRange',
                label: 'Dates',
                value: `${formatPretty(anyFilters?.dateFrom)} → ${formatPretty(
                    anyFilters?.dateTo,
                )}`,
            });
        }

        if (
            Array.isArray(anyFilters?.moduleType) &&
            anyFilters.moduleType.length
        ) {
            anyFilters.moduleType.forEach((mt: any) => {
                chips.push({
                    key: `moduleType:${String(mt)}`,
                    label: makeLabel('moduleType'),
                    value: String(mt),
                });
            });
        }

        if (anyFilters?.guest) {
            chips.push({
                key: 'guest',
                label: makeLabel('guest'),
                value: String(anyFilters.guest),
            });
        }

        if (!isWaitlistTabActive && anyFilters?.visitType) {
            chips.push({
                key: 'visitType',
                label: makeLabel('visitType'),
                value: String(anyFilters.visitType),
            });
        }

        if (anyFilters?.bookerType) {
            chips.push({
                key: 'bookerType',
                label: makeLabel('bookerType'),
                value: String(anyFilters.bookerType),
            });
        }

        return chips;
    }, [filters, searchValue, isWaitlistTabActive]);

    const removeAppliedFilter = (key: string) => {
        const next: any = { ...filters };

        if (key === 'search') {
            clearSearch();
            return;
        }

        if (key === 'dateRange') {
            next.dateFrom = undefined;
            next.dateTo = undefined;
            dispatch(setReservationFilters(next));
            return;
        }

        if (key.startsWith('moduleType:')) {
            const valueToRemove = key.split(':')[1];

            const nextModuleType = (next.moduleType || []).filter(
                (x: any) => String(x) !== String(valueToRemove),
            );

            next.moduleType = nextModuleType;

            setValue('moduleType', nextModuleType, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
            });

            dispatch(setReservationFilters(next));
            return;
        }

        if (key === 'guest') {
            next.guest = undefined;
            setValue('guest', undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
            });
            dispatch(setReservationFilters(next));
            return;
        }

        if (key === 'visitType') {
            next.visitType = undefined;
            setValue('visitType', undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
            });
            dispatch(setReservationFilters(next));
            return;
        }

        if (key === 'bookerType') {
            next.bookerType = undefined;
            setValue('bookerType', undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
            });
            dispatch(setReservationFilters(next));
            return;
        }

        next[key] = undefined;
        dispatch(setReservationFilters(next));
    };

    const clearAllAppliedFilters = () => {
        clearSearch();

        reset({
            moduleType: [],
            guest: undefined,
            visitType: undefined,
            bookerType: undefined,
        });

        dispatch(
            setReservationFilters({
                ...filters,
                moduleType: [],
                visitType: undefined,
                bookerType: undefined,
                guest: undefined,
                dateFrom: undefined,
                dateTo: undefined,
            }),
        );
    };

    const onClickNewReservation = (value: string) => {
        if (value === 'aiReservation') {
            navigate(`/reservations/ai-assistant`, {
                state: { page: 'reservationsPage', id: undefined },
            });
        } else {
            navigate(`/viewExperiences/all`);
        }
    };

    const pillTabsSx = {
        backgroundColor: alpha(theme.palette.common.black, 0.03),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        py: 3.5,
        minHeight: 44,
        paddingLeft: 1.2,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        '& .MuiTabs-indicator': { display: 'none' },
        '& .MuiTabs-scroller': {
            px: 0,
        },
        '& .MuiTabs-flexContainer': {
            height: 44,
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 0.5,
        },
    };

    const pillTabSx = {
        textTransform: 'none',
        height: 32,
        minHeight: 32,
        px: 1.25,
        borderRadius: 2,
        minWidth: 'fit-content',
        fontWeight: 800,
        color: theme.palette.text.secondary,
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.black, 0.05),
            color: theme.palette.text.primary,
        },
        '&.Mui-selected': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.main },
        },
    };

    const getCountBadgeSx = (selected: boolean) => ({
        px: 0.9,
        height: 20,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
        backgroundColor: selected
            ? alpha(theme.palette.common.white, 0.22)
            : alpha(theme.palette.common.black, 0.08),
        color: selected
            ? theme.palette.common.white
            : theme.palette.text.primary,
    });

    const renderTabLabel = (
        label: string,
        count: number,
        selected: boolean,
    ) => (
        <Stack direction='row' spacing={1} alignItems='center'>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: 14,
                    lineHeight: 1,
                    color: selected
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                }}
            >
                {label}
            </Typography>
            <Box sx={getCountBadgeSx(selected)}>{count}</Box>
        </Stack>
    );

    const viewMode = view ? 'calendar' : 'list';

    const handleViewModeChange = (
        _e: React.MouseEvent<HTMLElement>,
        next: 'list' | 'calendar' | null,
    ) => {
        if (!next) return;
        const params = new URLSearchParams(location.search);
        params.set('view', next);
        navigate({ search: params.toString() }, { replace: false });
    };

    return (
        <Grid
            sx={{
                width: '100%',
                height: isMobile ? '100%' : '80%',
                justifyContent: 'left',
                alignItems: 'center',
            }}
        >
            <Grid
                sx={{ width: '100%', justifyContent: 'left', display: 'flex' }}
            >
                <Typography
                    variant='h1'
                    sx={{ textAlign: 'left', textTransform: 'uppercase' }}
                >
                    Reservations
                </Typography>
            </Grid>

            <Grid
                container
                spacing={2}
                sx={{
                    width: '100%',
                    alignItems: 'flex-start',
                    mt: 1,
                }}
            >
                <Grid item xs={12} md={3} xl={2}>
                    <Paper
                        component='section'
                        aria-label='Reservation filters'
                        elevation={0}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            p: 1.5,
                            position: isMobile ? 'relative' : 'sticky',
                            top: 12,
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.02,
                            ),
                        }}
                    >
                        <Stack spacing={1.25}>
                            {!loggedInUser?.viewOnly && (
                                <Button
                                    onClick={() =>
                                        onClickNewReservation(
                                            loggedInUser?.role.includes(
                                                'owner',
                                            ) ||
                                                loggedInUser?.role.includes(
                                                    'guest',
                                                )
                                                ? 'aiReservation'
                                                : 'newReservation',
                                        )
                                    }
                                    variant='contained'
                                    startIcon={<AddIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        justifyContent: 'center',
                                        height: 40,
                                        fontWeight: 700,
                                    }}
                                    fullWidth
                                >
                                    New Reservation
                                </Button>
                            )}
                            {isGuest && (
                                <Button
                                    onClick={() => setCalendarModalOpen(true)}
                                    variant='outlined'
                                    startIcon={<LinkIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        justifyContent: 'center',
                                        height: 40,
                                        fontWeight: 800,
                                    }}
                                    fullWidth
                                >
                                    Calendar Link
                                </Button>
                            )}

                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                fullWidth
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: alpha(
                                        theme.palette.common.black,
                                        0.02,
                                    ),
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        height: 40,
                                        border: 0,
                                        borderRadius: 0,
                                        gap: 1,
                                    },
                                    '& .MuiToggleButton-root.Mui-selected': {
                                        backgroundColor:
                                            theme.palette.common.white,
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            >
                                <ToggleButton value='list'>
                                    <TableViewOutlinedIcon fontSize='small' />
                                    List
                                </ToggleButton>
                                <ToggleButton value='calendar'>
                                    <CalendarMonthIcon fontSize='small' />
                                    Calendar
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Divider sx={{ my: 0.25 }} />

                            {!view ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mt: 0.25,
                                        }}
                                    >
                                        <Typography
                                            variant='body3'
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Filters
                                        </Typography>

                                        <Button
                                            size='small'
                                            startIcon={
                                                <RestartAltIcon fontSize='small' />
                                            }
                                            onClick={clearAllAppliedFilters}
                                            disabled={
                                                appliedFilterChips.length === 0
                                            }
                                            aria-label='Reset all filters'
                                            sx={{
                                                minWidth: 'auto',
                                                px: 1,
                                                fontWeight: 600,
                                                color: theme.palette.text
                                                    .secondary,
                                                '&:hover': {
                                                    color: theme.palette.text
                                                        .primary,
                                                },
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Box>

                                    <TextField
                                        value={searchValue}
                                        onChange={(e) =>
                                            onSearchChange(e.target.value)
                                        }
                                        placeholder='Search reservations...'
                                        size='small'
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor:
                                                    theme.palette.neutral.main,
                                                height: 40,
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                py: 0,
                                                height: 40,
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                            '& .MuiInputAdornment-root': {
                                                height: '100%',
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <SearchIcon fontSize='small' />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchValue ? (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        onClick={clearSearch}
                                                        size='small'
                                                        sx={{ mr: -0.5 }}
                                                    >
                                                        <CloseIcon fontSize='small' />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined,
                                        }}
                                        fullWidth
                                    />

                                    <style>{getCss(theme)}</style>

                                    <Box
                                        sx={{ px: 0, width: '100%', mt: 0.75 }}
                                    >
                                        <Stack
                                            direction='row'
                                            spacing={1}
                                            sx={{ width: '100%' }}
                                        >
                                            <Button
                                                variant='outlined'
                                                onClick={openDatePopover}
                                                aria-label='From date filter'
                                                sx={{
                                                    borderRadius: 2,
                                                    justifyContent:
                                                        'space-between',
                                                    px: 1.25,
                                                    py: 0.6,
                                                    minHeight: 40,
                                                    height: 40,
                                                    borderColor:
                                                        theme.palette.divider,
                                                    backgroundColor:
                                                        theme.palette.common
                                                            .white,
                                                }}
                                                fullWidth
                                            >
                                                <Stack
                                                    spacing={0}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <Typography
                                                        aria-hidden='true'
                                                        sx={{
                                                            fontSize: 12,
                                                            color: 'text.secondary',
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        From
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: 12,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {formatDmy(
                                                            filters?.dateFrom,
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            </Button>

                                            <Button
                                                variant='outlined'
                                                onClick={openDatePopover}
                                                aria-label='To date filter'
                                                sx={{
                                                    borderRadius: 2,
                                                    justifyContent:
                                                        'space-between',
                                                    px: 1.25,
                                                    py: 0.6,
                                                    minHeight: 40,
                                                    height: 40,
                                                    borderColor:
                                                        theme.palette.divider,
                                                    backgroundColor:
                                                        theme.palette.common
                                                            .white,
                                                }}
                                                fullWidth
                                            >
                                                <Stack
                                                    spacing={0}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <Typography
                                                        aria-hidden='true'
                                                        sx={{
                                                            fontSize: 12,
                                                            color: 'text.secondary',
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        To
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: 12,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {formatDmy(
                                                            filters?.dateTo,
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            </Button>
                                        </Stack>

                                        <Popover
                                            open={isDatePopoverOpen}
                                            anchorEl={datePopoverAnchor}
                                            onClose={closeDatePopover}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            PaperProps={{
                                                sx: {
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    boxShadow: `0 12px 40px ${alpha(
                                                        theme.palette.common
                                                            .black,
                                                        0.14,
                                                    )}`,
                                                },
                                            }}
                                        >
                                            <DayPicker
                                                mode='range'
                                                numberOfMonths={
                                                    isMobile ? 1 : 2
                                                }
                                                selected={selectedRange}
                                                onSelect={(range) =>
                                                    handleSelectRange(range)
                                                }
                                            />

                                            <Stack
                                                direction='row'
                                                spacing={1}
                                                sx={{
                                                    pt: 1,
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <Button
                                                    variant='outlined'
                                                    onClick={() => {
                                                        dispatch(
                                                            setReservationFilters(
                                                                {
                                                                    ...filters,
                                                                    dateFrom:
                                                                        undefined,
                                                                    dateTo: undefined,
                                                                },
                                                            ),
                                                        );
                                                    }}
                                                    disabled={
                                                        !filters?.dateFrom &&
                                                        !filters?.dateTo
                                                    }
                                                >
                                                    Clear
                                                </Button>

                                                <Button
                                                    variant='contained'
                                                    onClick={closeDatePopover}
                                                >
                                                    Done
                                                </Button>
                                            </Stack>
                                        </Popover>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mt: 0.25,
                                        }}
                                    >
                                        <Typography
                                            variant='body3'
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Advanced Filters
                                        </Typography>
                                        <Button
                                            size='small'
                                            startIcon={
                                                <RestartAltIcon fontSize='small' />
                                            }
                                            onClick={() => {
                                                reset({
                                                    moduleType: [],
                                                    guest: undefined,
                                                    visitType: undefined,
                                                    bookerType: undefined,
                                                });
                                                dispatch(
                                                    setReservationFilters({
                                                        ...filters,
                                                        moduleType: [],
                                                        guest: undefined,
                                                        visitType: undefined,
                                                        bookerType: undefined,
                                                    }),
                                                );
                                            }}
                                            disabled={
                                                !filters?.moduleType?.length &&
                                                !filters?.guest &&
                                                !filters?.visitType &&
                                                !filters?.bookerType
                                            }
                                            aria-label='Reset advanced filters'
                                            sx={{
                                                minWidth: 'auto',
                                                px: 1,
                                                fontWeight: 600,
                                                color: theme.palette.text
                                                    .secondary,
                                                '&:hover': {
                                                    color: theme.palette.text
                                                        .primary,
                                                },
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Box>

                                    <FormProvider {...methods}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                px: 0,
                                                pt: 0.75,
                                                pb: 0.25,
                                                '& .MuiFormControl-root': {
                                                    width: '100%',
                                                    m: 0,
                                                },
                                                '& .MuiAutocomplete-root': {
                                                    width: '100%',
                                                },
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: `${theme.palette.neutral.main} !important`,
                                                    width: '100%',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: `${theme.palette.neutral.main} !important`,
                                                    borderRadius:
                                                        '8px !important',
                                                    height: '40px !important',
                                                    minHeight:
                                                        '40px !important',
                                                    width: '100%',
                                                },
                                                '& .MuiAutocomplete-inputRoot':
                                                    {
                                                        backgroundColor: `${theme.palette.neutral.main} !important`,
                                                        paddingTop:
                                                            '0px !important',
                                                        paddingBottom:
                                                            '0px !important',
                                                        minHeight:
                                                            '40px !important',
                                                        width: '100%',
                                                    },
                                                '& .MuiOutlinedInput-input': {
                                                    backgroundColor:
                                                        'transparent !important',
                                                    height: '40px !important',
                                                    paddingTop:
                                                        '0px !important',
                                                    paddingBottom:
                                                        '0px !important',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    boxSizing: 'border-box',
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiInputAdornment-root': {
                                                    height: '100%',
                                                },
                                                '& .MuiChip-root': {
                                                    backgroundColor: `${alpha('#000', 0.04)} !important`,
                                                },
                                            }}
                                        >
                                            <Stack spacing={1.25}>
                                                <FormMultiAutocomplete
                                                    name='moduleType'
                                                    placeholder='Experience type'
                                                    control={control}
                                                    options={[
                                                        {
                                                            value: 'property',
                                                            label: 'Property',
                                                        },
                                                        {
                                                            value: 'commercial',
                                                            label: 'Commercial',
                                                        },
                                                        {
                                                            value: 'boat',
                                                            label: 'Boat',
                                                        },
                                                        {
                                                            value: 'vehicle',
                                                            label: 'Vehicle',
                                                        },
                                                        {
                                                            value: 'activity',
                                                            label: 'Activity',
                                                        },
                                                    ]}
                                                />

                                                {!loggedInUser?.role.includes(
                                                    'guest',
                                                ) &&
                                                Array.isArray(guestCompanies) &&
                                                guestCompanies.length > 0 ? (
                                                    <FormAutocomplete
                                                        name='guest'
                                                        placeholder='Guest'
                                                        control={control}
                                                        options={guestCompanies.map(
                                                            (c: any) => ({
                                                                value: c.id,
                                                                label: c.name,
                                                            }),
                                                        )}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}

                                                {!isWaitlistTabActive ? (
                                                    <FormAutocomplete
                                                        name='visitType'
                                                        placeholder='Visit type'
                                                        control={control}
                                                        options={[
                                                            {
                                                                value: 'business',
                                                                label: 'Business',
                                                            },
                                                            {
                                                                value: 'personal',
                                                                label: 'Personal',
                                                            },
                                                        ]}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}

                                                {!loggedInUser?.role.includes(
                                                    'guest',
                                                ) ? (
                                                    <FormAutocomplete
                                                        name='bookerType'
                                                        placeholder='Reserved by'
                                                        control={control}
                                                        options={[
                                                            {
                                                                value: 'owner',
                                                                label: 'Owner',
                                                            },
                                                            {
                                                                value: 'guest',
                                                                label: 'Guest',
                                                            },
                                                        ]}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}
                                            </Stack>
                                        </Box>
                                    </FormProvider>

                                    <Divider sx={{ my: 0.25 }} />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant='body3'
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Active Filters
                                        </Typography>

                                        <Button
                                            size='small'
                                            onClick={clearAllAppliedFilters}
                                            disabled={
                                                appliedFilterChips.length === 0
                                            }
                                            sx={{ minWidth: 'auto', px: 1 }}
                                        >
                                            Clear all
                                        </Button>
                                    </Box>

                                    {appliedFilterChips.length === 0 ? (
                                        <Typography
                                            variant='body3'
                                            color='text.secondary'
                                        >
                                            None
                                        </Typography>
                                    ) : (
                                        <Stack
                                            direction='row'
                                            flexWrap='wrap'
                                            gap={1}
                                        >
                                            {appliedFilterChips.map((chip) => (
                                                <Chip
                                                    key={chip.key}
                                                    label={
                                                        chip.key === 'dateRange'
                                                            ? chip.value
                                                            : chip.key ===
                                                                'search'
                                                              ? `${chip.label}: ${chip.value}`
                                                              : `${chip.label}: ${capitalizeName(
                                                                    chip.value,
                                                                )}`
                                                    }
                                                    onDelete={() =>
                                                        removeAppliedFilter(
                                                            chip.key,
                                                        )
                                                    }
                                                    sx={{
                                                        borderRadius: 999,
                                                        fontWeight: 700,
                                                        backgroundColor: alpha(
                                                            theme.palette.common
                                                                .black,
                                                            0.04,
                                                        ),
                                                        border: `1px solid ${alpha(
                                                            theme.palette.common
                                                                .black,
                                                            0.08,
                                                        )}`,
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    )}

                                    <Divider />

                                    <Box sx={{ pt: 0.5 }}>
                                        <Button
                                            variant='outlined'
                                            startIcon={
                                                <FileDownloadOutlinedIcon />
                                            }
                                            onClick={openDownload}
                                            disabled={downloadDisabled}
                                            fullWidth
                                            sx={{
                                                borderRadius: 2,
                                                justifyContent: 'center',
                                                height: 40,
                                                fontWeight: 900,
                                            }}
                                        >
                                            Download list
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mt: 0.25,
                                        }}
                                    >
                                        <Typography
                                            variant='body3'
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Advanced Filters
                                        </Typography>
                                        <Button
                                            size='small'
                                            startIcon={
                                                <RestartAltIcon fontSize='small' />
                                            }
                                            onClick={() => {
                                                reset({
                                                    moduleType: [],
                                                    guest: undefined,
                                                    visitType: undefined,
                                                    bookerType: undefined,
                                                });
                                                dispatch(
                                                    setReservationFilters({
                                                        ...filters,
                                                        moduleType: [],
                                                        guest: undefined,
                                                        visitType: undefined,
                                                        bookerType: undefined,
                                                    }),
                                                );
                                            }}
                                            disabled={
                                                !filters?.moduleType?.length &&
                                                !filters?.guest &&
                                                !filters?.visitType &&
                                                !filters?.bookerType
                                            }
                                            sx={{
                                                minWidth: 'auto',
                                                px: 1,
                                                fontWeight: 600,
                                                color: theme.palette.text
                                                    .secondary,
                                                '&:hover': {
                                                    color: theme.palette.text
                                                        .primary,
                                                },
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Box>

                                    <FormProvider {...methods}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                px: 0,
                                                pt: 0.75,
                                                pb: 0.25,
                                                '& .MuiFormControl-root': {
                                                    width: '100%',
                                                    m: 0,
                                                },
                                                '& .MuiAutocomplete-root': {
                                                    width: '100%',
                                                },
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: `${theme.palette.neutral.main}  !important`,
                                                    width: '100%',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: `${theme.palette.neutral.main}  !important`,
                                                    borderRadius:
                                                        '8px !important',
                                                    height: '40px !important',
                                                    minHeight:
                                                        '40px !important',
                                                    width: '100%',
                                                },
                                                '& .MuiAutocomplete-inputRoot':
                                                    {
                                                        backgroundColor: `${theme.palette.neutral.main}  !important`,
                                                        paddingTop:
                                                            '0px !important',
                                                        paddingBottom:
                                                            '0px !important',
                                                        minHeight:
                                                            '40px !important',
                                                        width: '100%',
                                                    },
                                                '& .MuiOutlinedInput-input': {
                                                    backgroundColor:
                                                        'transparent !important',
                                                    height: '40px !important',
                                                    paddingTop:
                                                        '0px !important',
                                                    paddingBottom:
                                                        '0px !important',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    boxSizing: 'border-box',
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiInputAdornment-root': {
                                                    height: '100%',
                                                },
                                                '& .MuiChip-root': {
                                                    backgroundColor: `${alpha('#000', 0.04)} !important`,
                                                },
                                            }}
                                        >
                                            <Stack spacing={1.25}>
                                                <FormMultiAutocomplete
                                                    name='moduleType'
                                                    placeholder='Experience type'
                                                    control={control}
                                                    options={[
                                                        {
                                                            value: 'property',
                                                            label: 'Property',
                                                        },
                                                        {
                                                            value: 'commercial',
                                                            label: 'Commercial',
                                                        },
                                                        {
                                                            value: 'boat',
                                                            label: 'Boat',
                                                        },
                                                        {
                                                            value: 'vehicle',
                                                            label: 'Vehicle',
                                                        },
                                                        {
                                                            value: 'activity',
                                                            label: 'Activity',
                                                        },
                                                    ]}
                                                />

                                                {!loggedInUser?.role.includes(
                                                    'guest',
                                                ) &&
                                                Array.isArray(guestCompanies) &&
                                                guestCompanies.length > 0 ? (
                                                    <FormAutocomplete
                                                        name='guest'
                                                        placeholder='Guest'
                                                        control={control}
                                                        options={guestCompanies.map(
                                                            (c: any) => ({
                                                                value: c.id,
                                                                label: c.name,
                                                            }),
                                                        )}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}

                                                {!isWaitlistTabActive ? (
                                                    <FormAutocomplete
                                                        name='visitType'
                                                        placeholder='Visit type'
                                                        control={control}
                                                        options={[
                                                            {
                                                                value: 'business',
                                                                label: 'Business',
                                                            },
                                                            {
                                                                value: 'personal',
                                                                label: 'Personal',
                                                            },
                                                        ]}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}

                                                {!loggedInUser?.role.includes(
                                                    'guest',
                                                ) ? (
                                                    <FormAutocomplete
                                                        name='bookerType'
                                                        placeholder='Reserved by'
                                                        control={control}
                                                        options={[
                                                            {
                                                                value: 'owner',
                                                                label: 'Owner',
                                                            },
                                                            {
                                                                value: 'guest',
                                                                label: 'Guest',
                                                            },
                                                        ]}
                                                        disableClearable={
                                                            undefined
                                                        }
                                                    />
                                                ) : null}
                                            </Stack>
                                        </Box>
                                    </FormProvider>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={9} xl={10}>
                    {calendarView.view ? (
                        <ViewReservationsCalendarComponent />
                    ) : (
                        <Grid>
                            <Box
                                sx={{
                                    pt: 0,
                                    mt: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                                }}
                            >
                                <Tabs
                                    value={safeMainTabValue}
                                    onChange={handleChange}
                                    variant={
                                        isSmallTabs ? 'scrollable' : 'standard'
                                    }
                                    scrollButtons={false}
                                    sx={{
                                        ...pillTabsSx,
                                        flex: 1,
                                        minWidth: 0,
                                        '& .MuiTabs-scroller': { px: 0 },
                                        '& .MuiTabs-flexContainer': {
                                            paddingLeft: 0,
                                        },
                                    }}
                                >
                                    {tabsForUi.map((t) => (
                                        <Tab
                                            key={t.index}
                                            sx={pillTabSx}
                                            disableRipple
                                            label={renderTabLabel(
                                                t.label,
                                                t.count,
                                                safeMainTabValue === t.index,
                                            )}
                                            value={t.index}
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                            {isWaitlistTabActive && (
                                <Box sx={{ mt: 1.2 }}>
                                    <Tabs
                                        value={safeTabValue}
                                        onChange={handleChange}
                                        variant={
                                            isSmallTabs
                                                ? 'scrollable'
                                                : 'standard'
                                        }
                                        scrollButtons={false}
                                        sx={{
                                            ...pillTabsSx,
                                            minWidth: 0,
                                            '& .MuiTabs-scroller': { px: 0 },
                                            '& .MuiTabs-flexContainer': {
                                                paddingLeft: 0,
                                            },
                                        }}
                                    >
                                        {waitlistSubTabData.map((t) => (
                                            <Tab
                                                key={t.index}
                                                sx={pillTabSx}
                                                disableRipple
                                                label={renderTabLabel(
                                                    t.label,
                                                    t.count,
                                                    safeTabValue === t.index,
                                                )}
                                                value={t.index}
                                            />
                                        ))}
                                    </Tabs>
                                </Box>
                            )}

                            {(isFetching || isFetchingWaitlistRequests) && (
                                <Box sx={{ pt: 1.5 }}>
                                    <Skeleton
                                        variant='rectangular'
                                        height={52}
                                        sx={{ borderRadius: 2 }}
                                    />
                                </Box>
                            )}

                            <Popover
                                open={isDownloadOpen}
                                anchorEl={downloadAnchorEl}
                                onClose={closeDownload}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                PaperProps={{
                                    sx: {
                                        p: 1,
                                        borderRadius: 2,
                                        minWidth: 220,
                                    },
                                }}
                            >
                                <Stack spacing={1}>
                                    <Button
                                        onClick={() => {
                                            closeDownload();
                                            downloadCsv();
                                        }}
                                        startIcon={<TableViewOutlinedIcon />}
                                        variant='text'
                                        sx={{ justifyContent: 'flex-start' }}
                                    >
                                        Download CSV
                                    </Button>

                                    <Button
                                        onClick={() => {
                                            closeDownload();
                                            downloadPdf();
                                        }}
                                        startIcon={<PictureAsPdfOutlinedIcon />}
                                        variant='text'
                                        sx={{ justifyContent: 'flex-start' }}
                                    >
                                        Download PDF
                                    </Button>
                                </Stack>
                            </Popover>

                            {tabsForUi.map((tab) => (
                                <TabPanel
                                    key={tab.index}
                                    value={safeMainTabValue}
                                    index={tab.index}
                                >
                                    <Box
                                        sx={{
                                            minHeight: 400,
                                            width: '100%',
                                            overflowX: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            pt: 0,
                                        }}
                                    >
                                        <ReservationCalendarModal
                                            calendarId={
                                                profileDetails?.calendarId
                                            }
                                            open={calendarModalOpen}
                                            onClose={() =>
                                                setCalendarModalOpen(false)
                                            }
                                        />

                                        {filtersReady &&
                                        !isFetching &&
                                        !isFetchingWaitlistRequests ? (
                                            <Box sx={{ paddingTop: '15px' }}>
                                                <ViewReservationListComponent
                                                    tab={
                                                        isWaitlistTabActive
                                                            ? activeTabLabel
                                                            : tab.label
                                                    }
                                                    reservations={
                                                        listReservations
                                                    }
                                                    pageSize={
                                                        isLaptop ? 5 : 100
                                                    }
                                                    searchValue={searchValue}
                                                />
                                            </Box>
                                        ) : (
                                            <ReportLoadingComponent />
                                        )}
                                    </Box>
                                </TabPanel>
                            ))}
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
}
